
import { useState, useEffect } from 'react';
import { UserProfile, CurriculumData, CurriculumNode, SubjectType, Language } from '../types';
import { userService } from '../services/userService';
import { getInitialCurriculum } from '../utils/initialData';
import { supabase } from '../lib/supabaseClient';

export const useWonderEngine = (lang: Language) => {
  const [session, setSession] = useState<any>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [curriculum, setCurriculum] = useState<CurriculumData | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize Auth
  useEffect(() => {
    userService.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (!session) setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) {
         setUser(null);
         setCurriculum(null);
         setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load User Data with Fallback Initialization
  useEffect(() => {
    const loadData = async () => {
      if (!session) return;
      
      const isInitialSync = !user;
      if (isInitialSync) setLoading(true);
      
      const dbUser = await userService.getProfile();
      
      if (dbUser) {
        setUser(dbUser);
        
        // Load Curriculum
        const dbCurriculum = await userService.getCurriculum();
        if (dbCurriculum) {
          setCurriculum(dbCurriculum);
        } else {
          const defaultCurr = getInitialCurriculum(lang);
          await userService.saveCurriculum(defaultCurr);
          setCurriculum(defaultCurr);
        }
      }
      
      if (isInitialSync) setLoading(false);
    };

    if (session) {
      loadData();
    }
  }, [session, lang]);

  const updateCurriculum = (newCurriculum: CurriculumData) => {
    setCurriculum(newCurriculum);
    userService.saveCurriculum(newCurriculum);
  };

  const startLesson = (node: CurriculumNode): boolean => {
    if (!user) return false;
    const newEnergy = Math.max(0, user.energy - 20);
    setUser({ ...user, energy: newEnergy });
    userService.updateProfile({ energy: newEnergy });
    return true;
  };

  const completeLesson = async (node: CurriculumNode, subject: SubjectType) => {
    if (!user) return;
    
    const xpGain = 50;
    const currentSubjectScore = user.stats[subject] || 0;
    
    // Increment specific subject score and global task count
    const newStats = { 
      ...user.stats, 
      [subject]: Math.min(100, currentSubjectScore + 10),
      tasks_completed: (user.stats.tasks_completed || 0) + 1
    };

    const newSkills = Array.from(new Set([...user.skills, node.id]));
    const newXp = user.xp + xpGain;
    const newLevel = Math.floor(newXp / 200) + 1;

    const updatedUser = {
      ...user,
      xp: newXp,
      level: newLevel,
      skills: newSkills,
      stats: newStats
    };
    
    setUser(updatedUser);
    await userService.updateProfile(updatedUser);
    await userService.logLearning('lesson_complete', subject, xpGain, { nodeId: node.id });
  };

  const chargeEnergy = async (durationMinutes: number) => {
    if (!user) return;
    
    // durationMinutes represents the actual time spent focusing
    const newEnergy = Math.min(user.maxEnergy, user.energy + durationMinutes);
    const newStats = {
      ...user.stats,
      focus_minutes: (user.stats.focus_minutes || 0) + durationMinutes
    };

    const updatedUser = {
      ...user,
      energy: newEnergy,
      stats: newStats
    };

    setUser(updatedUser);
    await userService.updateProfile(updatedUser);
    await userService.logLearning('focus_session', 'general', 0, { duration: durationMinutes });
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return;
    const updated = { ...user, ...updates };
    setUser(updated);
    await userService.updateProfile(updates);
  };

  return {
    session,
    user,
    curriculum,
    loading,
    actions: {
      updateCurriculum,
      startLesson,
      completeLesson,
      chargeEnergy,
      updateProfile
    }
  };
};
